import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI, setStoredToken, getStoredToken } from '../utils/api';
import { Phone, CheckCircle, ArrowRight, Home, Shield } from 'lucide-react';
import { toast } from 'sonner';
import OtpResendButton from '../components/OtpResendButton';

const LOGO_URL = `${process.env.PUBLIC_URL}/branding/genzox-logo.png`;

const CompleteProfile = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // phone | otp | done
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (user.phone_verified) { navigate('/'); }
  }, [user, navigate]);

  const handleSendOTP = async () => {
    if (phone.length < 10) { toast.error('Enter a valid 10-digit number'); return; }
    setLoading(true);
    try {
      await authAPI.sendOTP(`+91${phone}`);
      setStep('otp');
      toast.success('OTP sent!');
    } catch (e) {
      setStep('otp');
      toast.info('Test mode — enter 123456');
    } finally { setLoading(false); }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    setLoading(true);
    try {
      const r = await authAPI.verifyOTP(`+91${phone}`, otp, user.name, user.email);
      if (r.data?.session_token) setStoredToken(r.data.session_token);
      const t = getStoredToken();
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/update-profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ phone: `+91${phone}`, phone_verified: true })
      });
      await checkAuth();
      setStep('done');
      toast.success('Phone verified!');
    } catch (e) {
      toast.error('Invalid OTP. Try again.');
    } finally { setLoading(false); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0edff] via-white to-[#fff4ed] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-3xl shadow-2xl shadow-[#1800ad]/5 p-6 sm:p-8" data-testid="complete-profile-card">
          <div className="text-center mb-6">
            <img src={LOGO_URL} alt="GenzoX" width="512" height="512" loading="eager" fetchpriority="high" decoding="async" className="h-12 w-auto mx-auto mb-4" />
            {step !== 'done' ? (
              <>
                <h1 className="text-xl sm:text-2xl font-bold text-[#0A0B10]" data-testid="title">Verify Your Phone</h1>
                <p className="text-xs text-[#9CA3AF] mt-1">One quick step to unlock all features</p>
              </>
            ) : (
              <>
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle size={28} className="text-green-600" />
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-[#0A0B10]" data-testid="done-title">You're All Set!</h1>
              </>
            )}
          </div>

          {/* User Card — shows Google name, email, picture */}
          <div className="bg-[#f8f7ff] rounded-2xl p-4 mb-5 border border-[#1800ad]/5" data-testid="user-info">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full border-2 border-white shadow-sm" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#1800ad] flex items-center justify-center text-white text-lg font-bold shadow-sm">
                  {user.name?.[0] || 'U'}
                </div>
              )}
              <div className="min-w-0">
                <p className="font-bold text-sm text-[#0A0B10] truncate" data-testid="user-name">{user.name}</p>
                <p className="text-xs text-[#6B7280] truncate" data-testid="user-email">{user.email}</p>
              </div>
              {user.email_verified && (
                <span className="ml-auto px-2 py-0.5 bg-blue-50 text-blue-600 text-[9px] font-bold rounded-full flex-shrink-0">Google</span>
              )}
            </div>
          </div>

          {/* Phone Input */}
          {step === 'phone' && (
            <div className="space-y-4">
              <div>
                <label className="flex items-center gap-1.5 text-xs font-bold text-[#0A0B10] mb-1.5"><Phone size={13} /> Mobile Number</label>
                <div className="flex gap-2">
                  <span className="flex items-center px-3 bg-[#f0edff] rounded-xl text-sm text-[#1800ad] font-bold">+91</span>
                  <input data-testid="phone-input" type="tel" value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit number" maxLength={10} autoFocus
                    className="flex-1 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none" />
                </div>
              </div>
              <button data-testid="send-otp-btn" onClick={handleSendOTP} disabled={loading || phone.length < 10}
                className="w-full bg-[#1800ad] text-white rounded-2xl py-3.5 font-bold text-sm hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20 disabled:opacity-40 flex items-center justify-center gap-2">
                {loading ? 'Sending...' : <><ArrowRight size={16} /> Send OTP</>}
              </button>

              {/* Why verify info box */}
              <div className="bg-[#fff8f3] rounded-xl p-3 border border-[#ff8500]/10">
                <p className="flex items-center gap-1.5 text-[10px] font-bold text-[#9A4500] mb-1"><Shield size={11} /> Why verify?</p>
                <ul className="text-[10px] text-[#6B7280] space-y-0.5 ml-4">
                  <li>Required to post ads</li>
                  <li>Builds trust with buyers</li>
                  <li>Prevents fake accounts</li>
                </ul>
              </div>

              <button data-testid="skip-btn" onClick={() => navigate('/')}
                className="w-full text-[#9CA3AF] text-xs font-medium hover:text-[#6B7280] transition-colors py-1">
                Skip for now
              </button>
            </div>
          )}

          {/* OTP Verify */}
          {step === 'otp' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#0A0B10] mb-1.5">Enter 6-digit OTP</label>
                <input data-testid="otp-input" type="text" value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="------" maxLength={6} autoFocus
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3.5 text-lg text-center tracking-[0.5em] font-bold text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none" />
                <p className="text-[10px] text-[#9CA3AF] mt-1 text-center">Sent to +91 {phone}</p>
              </div>
              <button data-testid="verify-otp-btn" onClick={handleVerifyOTP} disabled={loading || otp.length < 6}
                className="w-full bg-[#1800ad] text-white rounded-2xl py-3.5 font-bold text-sm hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20 disabled:opacity-40">
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
              {/* iter65 — Resend OTP with 30s countdown */}
              <div className="flex items-center justify-center">
                <OtpResendButton
                  onResend={async () => {
                    try {
                      await authAPI.sendOTP(`+91${phone}`);
                      toast.success('OTP resent to your phone');
                    } catch (e) {
                      const detail = e?.response?.data?.detail;
                      toast.error(detail || 'Failed to resend OTP. Try again.');
                      throw e;
                    }
                  }}
                  testIdPrefix="cp-resend"
                />
              </div>
              <button onClick={() => { setStep('phone'); setOtp(''); }}
                className="w-full text-[#9CA3AF] text-xs font-medium hover:text-[#1800ad] transition-colors py-1">
                Change number
              </button>
            </div>
          )}

          {/* Success */}
          {step === 'done' && (
            <div className="space-y-3">
              <div className="bg-green-50 rounded-xl p-3 text-center border border-green-100">
                <p className="text-xs font-bold text-green-700">+91 {phone} verified successfully</p>
              </div>
              <button data-testid="go-home-btn" onClick={() => navigate('/')}
                className="w-full bg-[#1800ad] text-white rounded-2xl py-3.5 font-bold text-sm hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20 flex items-center justify-center gap-2">
                <Home size={16} /> Go to Homepage
              </button>
              <button data-testid="go-dashboard-btn" onClick={() => navigate('/dashboard')}
                className="w-full bg-gray-100 text-[#0A0B10] rounded-2xl py-3.5 font-bold text-sm hover:bg-gray-200 transition-colors">
                Go to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
